import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const stats = [
  { icon: 'ri-book-open-line', value: '20,000+', label: '漫画作品' },
  { icon: 'ri-user-heart-line', value: '500万+', label: '月活跃读者' },
  { icon: 'ri-global-line', value: '150+', label: '覆盖国家/地区' },
  { icon: 'ri-file-copy-line', value: '100万+', label: '总章节数' },
];

const values = [
  {
    icon: 'ri-shield-check-line',
    title: '免费阅读',
    desc: '所有漫画永久免费，无隐藏收费，让每位漫画爱好者都能畅享阅读乐趣。',
  },
  {
    icon: 'ri-speed-up-line',
    title: '极速更新',
    desc: '与韩国同步更新，每日提供最新章节翻译，让你第一时间追更心仪作品。',
  },
  {
    icon: 'ri-translate',
    title: '专业翻译',
    desc: '拥有专业的中文翻译团队，确保每部作品的翻译质量和阅读流畅性。',
  },
  {
    icon: 'ri-device-line',
    title: '多端适配',
    desc: '完美适配手机、平板和电脑，随时随地享受流畅的阅读体验。',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: '读者至上',
    desc: '以读者体验为核心，持续优化平台功能，倾听每一位读者的声音。',
  },
  {
    icon: 'ri-sparkling-line',
    title: '内容丰富',
    desc: '涵盖七大品类，持续引进优质韩漫作品，满足不同读者的口味需求。',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setErrorMsg('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9pc84qftmtnfcfh0khg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setErrorMsg(msg);
      }
    } catch {
      setFormStatus('error');
      setErrorMsg('网络错误，请稍后重试');
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.freekcomic.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.freekcomic.com/about' },
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于免费韩漫大全',
    description: '免费韩漫大全是专业的韩国漫画在线阅读平台，提供全品类免费韩漫，每日更新，无需注册。',
    url: 'https://www.freekcomic.com/about',
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <Navbar />
      <main className="pt-16">
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20cozy%20library%20or%20reading%20space%20with%20soft%20lighting%20bookshelves%20modern%20Korean%20aesthetic%20minimalist%20design%20amber%20tones%20inviting%20atmosphere%20artistic%20composition&width=1920&height=800&seq=about-hero-manhwa&orientation=landscape"
              alt="关于免费韩漫大全"
              className="w-full h-full object-cover"
              title="关于免费韩漫大全"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
            <nav className="flex items-center gap-2 text-sm text-background-100/70 mb-4" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs w-3 h-3 flex items-center justify-center"></i>
              <span className="text-background-50">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 text-center">
              关于免费韩漫大全
            </h1>
            <p className="text-sm md:text-base text-background-100/70 mt-3 max-w-lg text-center">
              打造最优质的韩国漫画在线阅读平台
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-6">
              我们的故事
            </h2>
            <div className="prose-custom text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
              <p>
                <strong>免费韩漫大全</strong>成立于2020年，由一群热爱韩国漫画的年轻人创立。我们亲眼见证了韩国漫画在全球范围内的蓬勃发展，也深切感受到中文读者对于优质韩漫阅读体验的渴望。怀揣着「让每一位漫画爱好者都能轻松享受韩漫」的初心，我们打造了这个免费、便捷、内容丰富的韩国漫画在线阅读平台。
              </p>
              <p>
                从最初的几十部漫画发展至今，平台已收录超过<strong>两万部</strong>韩国漫画作品，涵盖热血、恋爱、奇幻、悬疑、搞笑、日常、恐怖等全品类，每月服务超过<strong>500万</strong>活跃读者。我们与韩国多家顶尖漫画工作室和平台建立了深度合作关系，确保内容持续稳定更新。
              </p>
              <p>
                我们深知，优秀的内容离不开专业的翻译和精心的运营。因此我们组建了一支经验丰富的<strong>中文翻译团队</strong>，确保每一部作品的翻译质量和阅读流畅性，让中国读者能够感受到与原文相同的阅读体验。
              </p>
              <p>
                未来，免费韩漫大全将继续秉持「读者至上」的理念，不断优化平台功能，拓展内容资源，为全球中文漫画爱好者提供更优质的韩国漫画在线阅读服务。我们相信，好的漫画值得被更多人看见。
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-12">
              平台数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${stat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <p className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-1">{stat.value}</p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-3">
              我们的承诺
            </h2>
            <p className="text-sm md:text-base text-foreground-500 text-center mb-12 max-w-lg mx-auto">
              六大核心价值，为每一位读者提供最好的韩漫阅读体验
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((v) => (
                <div key={v.title} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${v.icon} text-lg text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm md:text-base text-foreground-500 text-center mb-10">
              有任何问题、建议或合作意向，欢迎随时与我们取得联系
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                <p className="text-sm text-foreground-500">contact@freekcomic.com</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-line text-xl text-accent-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">办公地址</h4>
                <p className="text-sm text-foreground-500">韩国首尔特别市江南区</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-xl text-secondary-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                <p className="text-sm text-foreground-500">周一至周五 9:00-18:00</p>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
              <h3 className="text-lg font-bold font-heading text-foreground-900 mb-6">发送消息</h3>

              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </div>
                  <p className="text-foreground-900 font-semibold text-lg">消息已发送！</p>
                  <p className="text-sm text-foreground-500 mt-2">感谢你的来信，我们会在1-2个工作日内回复你。</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                        placeholder="你的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                      placeholder="消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容 *</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                      placeholder="请输入你要发送的消息内容，最多500字..."
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer text-sm disabled:opacity-60 whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                        发送中...
                      </span>
                    ) : '发送消息'}
                  </button>
                  {formStatus === 'error' && (
                    <p className="text-red-500 text-sm mt-2">{errorMsg}</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}